import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger,
} from "@/components/ui/select";
import { Loader2, ChevronDown, ChevronRight } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";

const inputCls = "rounded-sm border-slate-300 focus-visible:ring-2 focus-visible:ring-[#002FA7]";
const FLAG_KEYS = ["consumption_basis", "dimension", "inventory_mode", "track_inventory", "pack_size_variant", "has_designs", "has_recipe", "absorbs_losses"];
const DEFAULT_FLAGS = { consumption_basis: "per_bottle", dimension: "count", inventory_mode: "", track_inventory: true, pack_size_variant: false, has_designs: false, has_recipe: false, absorbs_losses: false };
const DIMS = { count: "Count (each)", volume: "Volume", weight: "Weight", length: "Length" };
const MODES = { "": "Auto (recommended)", discrete: "Discrete — units", film: "Film — rolls / weight", label: "Label — rolls / labels" };
const suggestMode = (f) => (f.has_designs ? "label" : (["weight", "length"].includes(f.dimension) ? "film" : "discrete"));

export default function ComponentDialog({ open, onOpenChange, onSubmit, initial, title }) {
  const [name, setName] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [description, setDescription] = useState("");
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [flags, setFlags] = useState(DEFAULT_FLAGS);
  const [defaultWeight, setDefaultWeight] = useState("");
  const [defaultWeightUnit, setDefaultWeightUnit] = useState("g");
  const [showBreakdown, setShowBreakdown] = useState(true);
  const [advanced, setAdvanced] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(initial?.name || "");
    setImageUrl(initial?.image_url || "");
    setDescription(initial?.description || "");
    setClassId(initial?.class_id || "");
    setFlags(initial ? { ...DEFAULT_FLAGS, ...Object.fromEntries(FLAG_KEYS.map((k) => [k, initial[k] ?? DEFAULT_FLAGS[k]])) } : DEFAULT_FLAGS);
    setDefaultWeight(initial?.default_weight_per_unit != null ? String(initial.default_weight_per_unit) : "");
    setDefaultWeightUnit(initial?.default_weight_unit || "g");
    setShowBreakdown(initial ? initial.show_pallet_breakdown !== false : true);
    setAdvanced(false);
    api.get("/component-classes").then(({ data }) => setClasses(data)).catch(() => {});
  }, [open, initial]);

  const pickClass = (id) => {
    setClassId(id);
    const cls = classes.find((c) => c.id === id);
    if (cls) setFlags(Object.fromEntries(FLAG_KEYS.map((k) => [k, cls[k] ?? DEFAULT_FLAGS[k]])));
  };
  const setFlag = (k, v) => setFlags((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      await onSubmit({ name: name.trim(), variation_label: "", image_url: imageUrl.trim(), description: description.trim(), class_id: classId, ...flags, show_pallet_breakdown: showBreakdown, default_weight_per_unit: defaultWeight === "" ? null : Number(defaultWeight), default_weight_unit: defaultWeightUnit });
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-sm sm:max-w-md" data-testid="component-dialog">
        <DialogHeader>
          <DialogTitle className="text-left font-heading tracking-tight">{title}</DialogTitle>
          <DialogDescription className="text-left">Pick a class to set how this component behaves. You can fine-tune below.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">Component Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Caps" className={inputCls} data-testid="component-name-input" autoFocus />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">Component Class</Label>
            <Select value={classId || undefined} onValueChange={pickClass}>
              <SelectTrigger className="rounded-sm" data-testid="component-class-select">
                <span className={classId ? "" : "text-slate-400"}>{classes.find((c) => c.id === classId)?.name || "Choose a class"}</span>
              </SelectTrigger>
              <SelectContent>
                {classes.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          {!["film", "label"].includes(flags.inventory_mode || suggestMode(flags)) && (
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">Inventory Display</Label>
              <Select value={showBreakdown ? "breakdown" : "qty"} onValueChange={(v) => setShowBreakdown(v === "breakdown")}>
                <SelectTrigger className="rounded-sm" data-testid="component-inventory-display-select">
                  <span>{showBreakdown ? "Pallets / Cases / Units breakdown" : "Quantity in stock only"}</span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="breakdown">Pallets / Cases / Units breakdown</SelectItem>
                  <SelectItem value="qty">Quantity in stock only</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-400">Controls how stock shows on the Inventory page and printouts. Roll-tracked items (film / labels) always show rolls & partials.</p>
            </div>
          )}

          <button type="button" onClick={() => setAdvanced((v) => !v)} className="flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 hover:text-slate-800" data-testid="component-advanced-toggle">
            {advanced ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} Behavior (advanced)
          </button>
          {advanced && (
            <div className="space-y-3 rounded-sm border border-slate-200 bg-slate-50 p-3" data-testid="component-flags">
              {!["film", "label"].includes(flags.inventory_mode) && (
              <div className="space-y-1">
                <span className="text-[11px] text-slate-500">Dimension</span>
                <Select value={flags.dimension} onValueChange={(v) => setFlag("dimension", v)}>
                  <SelectTrigger className="h-8 rounded-sm text-xs" data-testid="flag-dimension"><span>{DIMS[flags.dimension]}</span></SelectTrigger>
                  <SelectContent>{Object.entries(DIMS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              )}
              <div className="space-y-1">
                <span className="text-[11px] text-slate-500">Inventory / stock behavior</span>
                <Select value={flags.inventory_mode || "auto"} onValueChange={(v) => { const m = v === "auto" ? "" : v; setFlag("inventory_mode", m); if (m === "label") setFlag("dimension", "count"); else if (m === "film" && !["weight", "length"].includes(flags.dimension)) setFlag("dimension", "weight"); }}>
                  <SelectTrigger className="h-8 rounded-sm text-xs" data-testid="flag-inventory-mode"><span>{MODES[flags.inventory_mode || ""]}</span></SelectTrigger>
                  <SelectContent>{Object.entries(MODES).map(([k, v]) => (<SelectItem key={k || "auto"} value={k || "auto"}>{v}</SelectItem>))}</SelectContent>
                </Select>
                <p className="text-[10px] text-slate-400">Auto → <span className="font-semibold text-slate-500">{MODES[suggestMode(flags)]}</span>. Film & Label unlock roll-based Add Stock (enter rolls or roll weight).</p>
              </div>
              {[["track_inventory", "Track as inventory"], ["has_designs", "Has designs / artwork"], ["has_recipe", "Has recipe / ingredients"], ["absorbs_losses", "Absorbs production losses"]].map(([k, lbl]) => (
                <label key={k} className="flex items-center gap-2 text-xs text-slate-700" data-testid={`flag-${k}`}>
                  <input type="checkbox" checked={!!flags[k]} onChange={(e) => setFlag(k, e.target.checked)} className="h-4 w-4 accent-[#002FA7]" />
                  {lbl}
                </label>
              ))}
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">Weight per unit (optional)</Label>
            <div className="flex gap-2">
              <Input type="text" inputMode="decimal" value={defaultWeight} onChange={(e) => setDefaultWeight(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="e.g. 1.5" className={inputCls + " flex-1"} data-testid="component-default-weight" />
              <Select value={defaultWeightUnit} onValueChange={setDefaultWeightUnit}>
                <SelectTrigger className="w-20 rounded-sm" data-testid="component-default-weight-unit"><span>{defaultWeightUnit}</span></SelectTrigger>
                <SelectContent>{["g", "kg", "oz", "lb"].map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <p className="text-[10px] text-slate-400">Applies to every variant (e.g. all cap colors weigh the same). A variant's own weight, if set, takes precedence.</p>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">Photo</Label>
            <ImageUpload value={imageUrl} onChange={(v) => setImageUrl(v)} testId="component-image-upload" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional details about this component" className={inputCls} data-testid="component-description-input" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="component-cancel-button">Cancel</Button>
            <Button type="submit" disabled={loading || !name.trim()} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="component-save-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
